import { useMemo, useState } from 'react';
import { REPORT_PERMISSIONS } from '@/constants/permissions';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { ZM_SEARCH_PLACEHOLDER } from '@/constants/app.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { getTableEmptyText } from '@/utils/listSearch';
import { derDashboardInfiniteQueryOptions, downloadDerDashboard } from '../api/dashboards.api';
import { ExportButton } from '@/components/common/ExportButton';
import { getDerDashboardColumns } from '../components/DerDashboardColumns';
import { ReportingPeriodFilterPanel } from '../components/ReportingPeriodFilterPanel';
import { useReportingPeriodFilters } from '../hooks/useReportingPeriodFilters';

export function DerDashboardPage() {
  const controller = useReportingPeriodFilters('der');
  const { rows, firstPageMeta, isLoading, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(derDashboardInfiniteQueryOptions(controller.params));
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const expenseTypes = useMemo(() => firstPageMeta?.columns ?? [], [firstPageMeta]);
  const totals = firstPageMeta?.totals;
  const appliedSearchText = controller.params.search;
  const columns = useMemo(
    () => getDerDashboardColumns(expenseTypes, appliedSearchText, totals),
    [expenseTypes, appliedSearchText, totals],
  );

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="DER Dashboard"
        titleAddon={
          <DateRangeBadge
            onClick={() => setIsFilterOpen(true)}
            dateFrom={controller.params.dateFrom}
            dateTo={controller.params.dateTo}
          />
        }
        search={{
          value: controller.searchText,
          onChange: controller.setSearchText,
          placeholder: ZM_SEARCH_PLACEHOLDER,
        }}
        actions={
          <>
            <AdvancedFilterButton
              isOpen={isFilterOpen}
              activeCount={controller.activeFilterCount}
              onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
            />
            <ExportButton
              requires={REPORT_PERMISSIONS.DER_DASHBOARD_EXPORT}
              onExport={() => downloadDerDashboard(controller.params)}
              disabled={isLoading || loadError !== null}
            />
          </>
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <ReportingPeriodFilterPanel controller={controller} />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the dashboard"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          isLoading={isLoading}
          className="lg:flex-1"
          stickyColumnId="name"
          {...getTableEmptyText(
            appliedSearchText,
            'No expenses for these filters',
            'Try a wider date range.',
          )}
          {...loadMoreProps}
        />
      )}
    </div>
  );
}
